import { usePageRefreshKey } from '../../../../../context/PageRefreshContext';
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Icon } from '@iconify/react';
import { getInvoiceTypes, createInvoiceType, updateInvoiceType, deleteInvoiceType } from "../../../../../redux/actions/SocietyPortal/InvoiceTypeAction/InvoiceTypeAction";
import { selectedSocietyStorer } from "../../../../../utils/webStorer";
import { PaginationLimit } from "../../../../../components/PaginationLimit";
import BillingTypesModal from "./BillingTypesModal";
import { SOCIETY_PORTAL, SOCIETY_PORTAL_INVOICES } from "../../../../../Routing/routes";

const BillingTypes = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { invoiceTypesData, loading, createLoading, updateLoading, deleteLoading, pagination } = useSelector(state => state.InvoiceTypeReducers);

    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({ invoice_title: "", invoice_type: "BILLING", frequency: "1" });
    const [editId, setEditId] = useState(null);
    const [viewMode, setViewMode] = useState("card");

    // Get society_id from localStorage
    const societyId = selectedSocietyStorer.getSocietyId();
    const [searchParams, setSearchParams] = useState({ page: 1, limit: 10, invoice_type: "", society_id: societyId });

    // Frequency mapping: API value -> Display value
    const frequencyMap = {
        "-1": "One-time",
        "1": "Monthly",
        "2": "Bi-Monthly",
        "3": "Quarterly",
        "6": "Half-Yearly",
        "12": "Yearly"
    };

    // Reverse mapping: Display value -> API value
    // eslint-disable-next-line no-unused-vars
    const _frequencyReverseMap = {
        "One-time": "-1",
        "Monthly": "1",
        "Bi-Monthly": "2",
        "Quarterly": "3",
        "Half-Yearly": "6",
        "Yearly": "12"
    };

    const categories = ["BILLING", "QUICK"];
    // eslint-disable-next-line no-unused-vars
    const _frequencies = ["One-time", "Monthly", "Bi-Monthly", "Quarterly", "Half-Yearly", "Yearly"];

    // Fetch invoice types on mount and when search params change
    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        dispatch(getInvoiceTypes(searchParams));
    }, [dispatch, searchParams, refreshKey]);

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {
            ...formData,
            // Quick bills have no billing cycle - stored as one-time.
            frequency: formData.invoice_type === 'QUICK' ? '-1' : formData.frequency,
            society_id: societyId,
            onSuccess: () => {
                handleCloseModal();
                dispatch(getInvoiceTypes(searchParams));
            }
        };

        if (editId) {
            dispatch(updateInvoiceType({ id: editId, ...payload }));
        } else {
            dispatch(createInvoiceType(payload));
        }
    };

    const handleEdit = (item) => {
        setFormData({
            invoice_title: item.invoice_title || "",
            invoice_type: item.invoice_type || "BILLING",
            frequency: item.frequency || "1"
        });
        setEditId(item.invoice_type_id);
        setShowModal(true);
    };

    const handleDelete = (id) => {
        if (window.confirm("Are you sure you want to delete this billing type?")) {
            dispatch(deleteInvoiceType({
                id,
                society_id: societyId,
                onSuccess: () => {
                    dispatch(getInvoiceTypes(searchParams));
                }
            }));
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setFormData({ invoice_title: "", invoice_type: "BILLING", frequency: "1" });
        setEditId(null);
    };

    const handleInlineUpdate = (item, field, value) => {
        // Category switch: QUICK drops the frequency (one-time), leaving QUICK restores monthly.
        const extra = field === 'invoice_type'
            ? (value === 'QUICK' ? { frequency: '-1' } : (item.invoice_type === 'QUICK' ? { frequency: '1' } : {}))
            : {};
        dispatch(updateInvoiceType({
            id: item.invoice_type_id,
            [field]: value,
            ...extra,
            society_id: societyId,
            onSuccess: () => {
                dispatch(getInvoiceTypes(searchParams));
            }
        }));
    };

    const getFrequencyColor = (frequency) => {
        const displayFrequency = frequencyMap[frequency] || frequency;
        switch (displayFrequency) {
            case 'Monthly': return { bg: '#dcfce7', color: '#16a34a' };
            case 'Bi-Monthly': return { bg: '#e0e7ff', color: '#4f46e5' };
            case 'Quarterly': return { bg: '#fef3c7', color: '#d97706' };
            case 'Half-Yearly': return { bg: '#fce7f3', color: '#db2777' };
            case 'Yearly': return { bg: '#fee2e2', color: '#dc2626' };
            case 'One-time': return { bg: '#f3e8ff', color: '#9333ea' };
            default: return { bg: '#f3f4f6', color: '#6b7280' };
        }
    };

    const getCategoryIcon = (category) => {
        return category === 'BILLING' ? 'mdi:receipt-text-outline' : 'mdi:flash-outline';
    };

    const billingTypes = invoiceTypesData || [];

    return (
        <div className="invoice-setup-page">
            {/* Page Header */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-12">
                <div>
                    <h6 className="fw-semibold mb-0 text-base">Billing Types</h6>
                    <p className="text-secondary-light text-sm mb-0 mt-1">
                        Configure billing types for different charges in your society
                    </p>
                </div>
            </div>

            {/* Info Banner */}
            <div className="card radius-8 border-0 mb-16 bg-primary-50">
                <div className="card-body p-12 d-flex align-items-center gap-3">
                    <div className="w-40-px h-40-px rounded-circle bg-primary-100 d-flex align-items-center justify-content-center flex-shrink-0">
                        <Icon icon="mdi:information-outline" className="text-primary-600 text-base" />
                    </div>
                    <p className="text-xs text-secondary-light mb-0">
                        <strong className="text-primary-600">Note:</strong> Billing types define the categories of charges applied to members.
                        Each type can have its own frequency and day book configuration.
                    </p>
                </div>
            </div>

            {/* Action Bar with Add Button and View Toggle */}
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-12">
                <button
                    onClick={() => setShowModal(true)}
                    className="btn d-flex align-items-center gap-2 px-10 py-4 radius-6 text-white"
                    style={{ backgroundColor: '#f59e0b' }}
                    disabled={loading}
                >
                    <Icon icon="mdi:plus-circle" className="text-base" />
                    Add New Billing Type
                </button>

                {/* View Toggle */}
                <div className="btn-group" role="group">
                    <button
                        type="button"
                        className={`btn d-flex align-items-center gap-2 px-16 py-10 ${viewMode === 'card' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setViewMode('card')}
                    >
                        <Icon icon="mdi:view-grid" className="text-lg" />
                        <span className="d-none d-sm-inline">Card</span>
                    </button>
                    <button
                        type="button"
                        className={`btn d-flex align-items-center gap-2 px-16 py-10 ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setViewMode('list')}
                    >
                        <Icon icon="mdi:view-list" className="text-lg" />
                        <span className="d-none d-sm-inline">List</span>
                    </button>
                </div>
            </div>

            {/* Loading State */}
            {loading && (
                <div className="d-flex justify-content-center align-items-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            )}

            {/* Card View */}
            {!loading && viewMode === 'card' && (
                <div className="row gy-4">
                    {billingTypes.map((item, index) => {
                        const freqColor = getFrequencyColor(item.frequency);
                        return (
                            <div key={item.invoice_type_id} className="col-xxl-4 col-lg-6">
                                <div className="card radius-12 border-0 h-100 shadow-sm hover-shadow-lg transition-all">
                                    {/* Card Header */}
                                    <div className="card-header py-16 px-20 bg-base border-bottom d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center gap-3">
                                            <div
                                                className="w-44-px h-44-px rounded-circle d-flex align-items-center justify-content-center"
                                                style={{ backgroundColor: item.invoice_type === 'QUICK' ? '#fef3c7' : '#dcfce7' }}
                                            >
                                                <Icon
                                                    icon={getCategoryIcon(item.invoice_type)}
                                                    className="text-2xl"
                                                    style={{ color: item.invoice_type === 'QUICK' ? '#d97706' : '#16a34a' }}
                                                />
                                            </div>
                                            <div>
                                                <span className="badge bg-neutral-100 text-secondary-light text-xs fw-medium px-10 py-4 radius-4">
                                                    S No.: {String(index + 1).padStart(2, '0')}
                                                </span>
                                                <h6 className="fw-semibold mb-0 mt-1">{item.invoice_title}</h6>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleDelete(item.invoice_type_id)}
                                            className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1 px-12 py-6 radius-6"
                                            disabled={deleteLoading}
                                        >
                                            <Icon icon="mdi:trash-can-outline" className="text-lg" />
                                            <span className="d-none d-xl-inline">Delete</span>
                                        </button>
                                    </div>

                                    {/* Card Body */}
                                    <div className="card-body p-20">
                                        <div className="row gy-3">
                                            {/* Day Book - Read Only (generated by backend) */}
                                            <div className="col-6">
                                                <label className="form-label text-secondary-light text-xs fw-medium mb-1">
                                                    Day Book
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control radius-8 h-44-px bg-neutral-100"
                                                    value={item.daybook || ""}
                                                    readOnly
                                                    disabled
                                                />
                                            </div>

                                            {/* Title */}
                                            <div className="col-6">
                                                <label className="form-label text-secondary-light text-xs fw-medium mb-1">
                                                    Title
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control radius-8 h-44-px bg-neutral-50"
                                                    value={item.invoice_title || ""}
                                                    onChange={(e) => handleInlineUpdate(item, 'invoice_title', e.target.value)}
                                                    placeholder="e.g., Monthly Maintenance"
                                                />
                                            </div>

                                            {/* Category */}
                                            <div className="col-6">
                                                <label className="form-label text-secondary-light text-xs fw-medium mb-1">
                                                    Category
                                                </label>
                                                <select
                                                    className="form-select radius-8 h-44-px bg-neutral-50"
                                                    value={item.invoice_type || "BILLING"}
                                                    onChange={(e) => handleInlineUpdate(item, 'invoice_type', e.target.value)}
                                                >
                                                    {categories.map(cat => (
                                                        <option key={cat} value={cat}>{cat}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* Frequency */}
                                            <div className="col-6">
                                                <label className="form-label text-secondary-light text-xs fw-medium mb-1">
                                                    Frequency
                                                </label>
                                                {item.invoice_type === 'QUICK' ? (
                                                    <input
                                                        className="form-control radius-8 h-44-px bg-neutral-50"
                                                        value="Not applicable"
                                                        disabled
                                                        title="Quick bills are raised as needed and have no billing frequency"
                                                    />
                                                ) : (
                                                    <select
                                                        className="form-select radius-8 h-44-px bg-neutral-50"
                                                        value={item.frequency || "1"}
                                                        onChange={(e) => handleInlineUpdate(item, 'frequency', e.target.value)}
                                                    >
                                                        {Object.entries(frequencyMap).map(([value, label]) => (
                                                            <option key={value} value={value}>{label}</option>
                                                        ))}
                                                    </select>
                                                )}
                                            </div>
                                        </div>

                                        {/* Stats Row */}
                                        <div className="mt-16 p-16 bg-neutral-50 radius-8">
                                            <div className="d-flex align-items-center justify-content-between">
                                                <div className="d-flex align-items-center gap-2">
                                                    <Icon icon="mdi:calendar-clock" className="text-base" style={{ color: freqColor.color }} />
                                                    <span className="text-xs text-secondary-light">Billing Frequency</span>
                                                </div>
                                                <span
                                                    className="badge px-12 py-6 radius-4 fw-medium"
                                                    style={{ backgroundColor: freqColor.bg, color: freqColor.color }}
                                                >
                                                    {item.invoice_type === 'QUICK' ? 'Not applicable' : (frequencyMap[item.frequency] || item.frequency)}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="mt-20 d-flex flex-wrap gap-2">
                                            <button
                                                onClick={() => handleEdit(item)}
                                                className="btn btn-primary flex-grow-1 d-flex align-items-center justify-content-center gap-2 py-10 radius-8"
                                            >
                                                <Icon icon="mdi:pencil" className="text-lg" />
                                                <span className="text-sm">Edit Details</span>
                                            </button>
                                            <button
                                                onClick={() => navigate(`${SOCIETY_PORTAL}${SOCIETY_PORTAL_INVOICES}?invoice_type_id=${item.invoice_type_id}`)}
                                                className="btn btn-outline-info flex-grow-1 d-flex align-items-center justify-content-center gap-2 py-10 radius-8"
                                            >
                                                <Icon icon="mdi:chart-line" className="text-lg" />
                                                <span className="text-sm">View Bills</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {/* Empty State */}
                    {billingTypes.length === 0 && (
                        <div className="col-12">
                            <div className="card radius-12 border-0 shadow-sm">
                                <div className="card-body p-24 text-center">
                                    <div className="w-80-px h-80-px rounded-circle bg-neutral-100 d-flex align-items-center justify-content-center mx-auto mb-20">
                                        <Icon icon="mdi:receipt-text-outline" className="text-secondary-light text-2xl" />
                                    </div>
                                    <h6 className="fw-semibold mb-2">No Billing Types Added Yet</h6>
                                    <p className="text-secondary-light text-sm mb-20">
                                        Get started by adding your first billing type to configure charges.
                                    </p>
                                    <button
                                        onClick={() => setShowModal(true)}
                                        className="btn btn-primary d-inline-flex align-items-center gap-2 px-20 py-10 radius-8"
                                    >
                                        <Icon icon="mdi:plus-circle" className="text-base" />
                                        Add First Billing Type
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* List View */}
            {!loading && viewMode === 'list' && (
                <div className="card radius-12 border-0 shadow-sm">
                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-hover mb-0">
                                <thead className="bg-neutral-50">
                                    <tr>
                                        <th className="ps-20 py-8 text-secondary-light text-xs fw-semibold">#</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Day Book</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Title</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Category</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Frequency</th>
                                        <th className="pe-20 py-8 text-secondary-light text-xs fw-semibold text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {billingTypes.map((item, index) => {
                                        const freqColor = getFrequencyColor(item.frequency);
                                        return (
                                            <tr key={item.invoice_type_id}>
                                                <td className="ps-12 py-8 align-middle">
                                                    <span className="text-secondary-light fw-medium">{String(index + 1).padStart(2, '0')}</span>
                                                </td>
                                                <td className="py-16 align-middle">
                                                    <span className="badge bg-neutral-100 text-secondary-light fw-medium px-12 py-6 radius-4">
                                                        {item.daybook}
                                                    </span>
                                                </td>
                                                <td className="py-16 align-middle">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <div
                                                            className="w-32-px h-32-px rounded-circle d-flex align-items-center justify-content-center"
                                                            style={{ backgroundColor: item.invoice_type === 'QUICK' ? '#fef3c7' : '#dcfce7' }}
                                                        >
                                                            <Icon
                                                                icon={getCategoryIcon(item.invoice_type)}
                                                                className="text-lg"
                                                                style={{ color: item.invoice_type === 'QUICK' ? '#d97706' : '#16a34a' }}
                                                            />
                                                        </div>
                                                        <span className="fw-medium">{item.invoice_title}</span>
                                                    </div>
                                                </td>
                                                <td className="py-16 align-middle">
                                                    <span className={`badge px-12 py-6 radius-4 fw-medium ${item.invoice_type === 'BILLING' ? 'bg-success-50 text-success-600' : 'bg-warning-50 text-warning-600'}`}>
                                                        {item.invoice_type}
                                                    </span>
                                                </td>
                                                <td className="py-16 align-middle">
                                                    <span
                                                        className="badge px-12 py-6 radius-4 fw-medium"
                                                        style={{ backgroundColor: freqColor.bg, color: freqColor.color }}
                                                    >
                                                        {item.invoice_type === 'QUICK' ? 'Not applicable' : (frequencyMap[item.frequency] || item.frequency)}
                                                    </span>
                                                </td>
                                                <td className="pe-12 py-8 align-middle text-end">
                                                    <div className="d-flex align-items-center justify-content-end gap-2">
                                                        <button
                                                            onClick={() => handleEdit(item)}
                                                            className="btn btn-sm btn-primary d-flex align-items-center gap-1 px-12 py-6 radius-6"
                                                        >
                                                            <Icon icon="mdi:pencil" className="text-lg" />
                                                            <span className="d-none d-lg-inline">Edit</span>
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(item.invoice_type_id)}
                                                            className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1 px-12 py-6 radius-6"
                                                            disabled={deleteLoading}
                                                        >
                                                            <Icon icon="mdi:trash-can-outline" className="text-lg" />
                                                            <span className="d-none d-lg-inline">Delete</span>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Empty State for List View */}
                        {billingTypes.length === 0 && (
                            <div className="p-40 text-center">
                                <div className="w-80-px h-80-px rounded-circle bg-neutral-100 d-flex align-items-center justify-content-center mx-auto mb-20">
                                    <Icon icon="mdi:receipt-text-outline" className="text-secondary-light text-2xl" />
                                </div>
                                <h6 className="fw-semibold mb-2">No Billing Types Added Yet</h6>
                                <p className="text-secondary-light text-sm mb-20">
                                    Get started by adding your first billing type to configure charges.
                                </p>
                                <button
                                    onClick={() => setShowModal(true)}
                                    className="btn btn-primary d-inline-flex align-items-center gap-2 px-20 py-10 radius-8"
                                >
                                    <Icon icon="mdi:plus-circle" className="text-base" />
                                    Add First Billing Type
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Pagination */}
            {!loading && pagination && pagination.total > 0 && (
                <PaginationLimit
                    currentPage={pagination.page || searchParams.page}
                    totalRecords={pagination.total || 0}
                    limit={searchParams.limit}
                    handleLimitChange={(e) => setSearchParams({ ...searchParams, limit: parseInt(e.target.value), page: 1 })}
                    handlePageChange={(page) => setSearchParams({ ...searchParams, page })}
                />
            )}

            {/* Add/Edit Modal */}
            <BillingTypesModal
                show={showModal}
                handleClose={handleCloseModal}
                handleSubmit={handleSubmit}
                formData={formData}
                setFormData={setFormData}
                editId={editId}
                categories={categories}
                frequencyMap={frequencyMap}
                loading={createLoading || updateLoading}
            />

            {/* Custom Styles */}
            <style>{`
                .hover-shadow-lg:hover {
                    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1) !important;
                    transform: translateY(-2px);
                }

                .transition-all {
                    transition: all 0.3s ease;
                }

                .w-32-px { width: 32px; }
                .h-32-px { height: 32px; }
                .w-40-px { width: 40px; }
                .h-40-px { height: 40px; }
                .w-44-px { width: 44px; }
                .h-44-px { height: 44px; }
                .w-80-px { width: 80px; }
                .h-80-px { height: 80px; }

                .card-body .btn {
                    font-weight: 500;
                }

                .table > :not(caption) > * > * {
                    border-bottom-color: var(--bs-border-color);
                }
            `}</style>
        </div>
    );
};

export default BillingTypes;
